import { Label } from "@noones/ui";
import { formatDateUTC } from "@pages/status-page/helpers/formatDate";
import { getGroupedComponents } from "@pages/status-page/helpers/groupComponents";
import { useSummaryData } from "@pages/status-page/hooks/useSummaryData";
import { IncidentLevelEnum, IncidentType } from "@store/types";
import React, { ReactNode, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { messages } from "./translations";
import { useUnresolvedIncidents } from "@pages/status-page/hooks/useUnresolvedIncidents";
import {
    compareIncidentByLevel,
    mapApiIncidentToIncident,
} from "@pages/status-page/helpers/incidents";
import { tw } from "@noones/ui/tw";
import { LanguagePicker } from "@modules/i18n/ui/LanguagePicker";

type ComponentGroupsType = {
    [key: string]: string;
};

type UnresolvedIncidentsCardPropsType = {
    className?: string;
    groups?: ComponentGroupsType;
    incident: IncidentType;
};

type LevelTagType = {
    children: ReactNode;
};

const levelTagMap: Record<IncidentLevelEnum, LevelTagType> = {
    [IncidentLevelEnum.SUCCESS]: {
        children: <FormattedMessage {...messages.successLevel} />,
    },
    [IncidentLevelEnum.WARNING]: {
        children: <FormattedMessage {...messages.warningLevel} />,
    },
    [IncidentLevelEnum.DANGER]: {
        children: <FormattedMessage {...messages.dangerLevel} />,
    },
};

export const UnresolvedIncidentCard = ({ groups, incident }: UnresolvedIncidentsCardPropsType) => {
    const componentsGroupIds: Array<string | null | undefined> = incident.components
        .map((component) => component.group_id)
        .filter((groupId, index, arr) => arr.indexOf(groupId) === index)
        .map((name) => groups && name && groups[name]);

    return (
        <div className={tw.flex.flex_col.gap_2.bg_white.p_4.rounded_xl}>
            <div className={tw.flex.items_center.gap_2}>
                <div className={tw.flex.items_center.flex_wrap.gap_1}>
                    {componentsGroupIds?.map(
                        (groupName) =>
                            groupName && (
                                <Label size={18} mode="grey" key={groupName}>
                                    {groupName}
                                </Label>
                            ),
                    )}
                </div>

                <Label size={18} mode="light-green" className={tw.shrink_0.space_x_1}>
                    <div>{levelTagMap[incident.level].children}</div>
                </Label>

                <span className={tw.text_body_12.font_normal.text_grey_green.shrink_0}>
                    <div>{formatDateUTC(incident.updatedAt)}</div>
                </span>
            </div>

            <div className={tw.flex.flex_col.gap_2}>
                <div className={tw.text_h4.font_semibold.text_green}>{incident.name}</div>

                {incident.description && (
                    <span className={tw.text_body_14.text_black.font_normal}>
                        {incident.description}
                    </span>
                )}
            </div>
        </div>
    );
};

export const UnresolvedIncidentCards = () => {
    const { data: summary } = useSummaryData();

    const { incidents } = useUnresolvedIncidents<IncidentType>(
        compareIncidentByLevel,
        mapApiIncidentToIncident,
    );

    const groupedComponents = useMemo(
        () => summary && getGroupedComponents(summary?.components),
        [summary],
    );

    const componentGroups =
        groupedComponents &&
        Object.keys(groupedComponents).reduce<ComponentGroupsType>((acc, item) => {
            const groupItem = groupedComponents[item][0];
            const groupId = groupItem.group_id;

            return groupItem && groupId !== null ? { ...acc, [groupId]: item } : acc;
        }, {});

    if (incidents.length === 0) {
        return null;
    }

    return (
        <div>
            <div className={tw.flex.items_center.justify_between}>
                <h2>
                    <FormattedMessage {...messages.title} />
                </h2>

                <LanguagePicker />
            </div>

            <div className={tw.space_y_4}>
                {incidents.map((incident) => (
                    <UnresolvedIncidentCard
                        groups={componentGroups}
                        incident={incident}
                        key={incident.id}
                    />
                ))}
            </div>
        </div>
    );
};
